import { Divider, GridLegacy, Stack, Typography } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { actionButtonSpacing } from 'constants/themeConstants';
import Chip, { ChipColor } from 'ui-component/extended/Chip';
import ProgressChip from 'ui-component/extended/Chip/ProgressChip';
import { detailTooltipRenderCell } from 'ui-component/DataGrid/Render/DetailTooltipRenderCell';

const AllocationChip = ({
  totalAllocated,
  total,
  minimumTotal,
  unitAbbreviation,
}: {
  totalAllocated: number;
  total: number;
  minimumTotal: number;
  unitAbbreviation?: string;
}) => {
  const minimumAdditionalToAllocate = Math.max(
    minimumTotal - totalAllocated,
    0
  );
  const targetAdditionalAllocation = Math.max(
    total - totalAllocated - minimumAdditionalToAllocate,
    0
  );

  return (
    <GridLegacy sx={{ overflow: 'hidden' }}>
      {totalAllocated === total || totalAllocated === 0 ? (
        <Chip
          label={`${totalAllocated.toLocaleString(
            'en-US'
          )}/${total.toLocaleString('en-US')}${
            unitAbbreviation ? ` ${unitAbbreviation}` : ''
          }`}
          chipcolor={totalAllocated > 0 ? ChipColor.success : ChipColor.error}
        />
      ) : (
        <ProgressChip
          sections={[
            {
              chipColor: ChipColor.success,
              amount: totalAllocated,
            },
            {
              chipColor: ChipColor.error,
              amount: minimumAdditionalToAllocate,
            },
            {
              chipColor: ChipColor.warning,
              amount: targetAdditionalAllocation,
            },
          ]}
          label={`${totalAllocated.toLocaleString(
            'en-US'
          )}/${total.toLocaleString('en-US')}${
            unitAbbreviation ? ` ${unitAbbreviation}` : ''
          }`}
          style={{ textTransform: 'uppercase' }}
        />
      )}
    </GridLegacy>
  );
};

const AllocationTooltip = ({
  allocated,
  consumed,
  total,
  minimumTotal,
  consumedLabel,
}: {
  allocated: number;
  consumed: number;
  total: number;
  minimumTotal: number;
  consumedLabel?: string;
}) => {
  const totalAllocated = allocated + consumed;
  const minimumAdditionalToAllocate = Math.max(
    minimumTotal - totalAllocated,
    0
  );
  const targetAdditionalAllocation = Math.max(
    total - totalAllocated - minimumAdditionalToAllocate,
    0
  );
  return (
    <GridLegacy container spacing={0} sx={{ p: 2 }}>
      <Stack
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={actionButtonSpacing}
        divider={<Divider flexItem />}
      >
        {minimumAdditionalToAllocate > 0 && (
          <Stack
            justifyContent="flex-start"
            alignItems="center"
            spacing={10}
            direction="row"
          >
            <Chip
              label="Need to Be Allocated"
              style={{ textTransform: 'uppercase' }}
              chipcolor={ChipColor.error}
            />
            <Typography variant="h5">
              {minimumAdditionalToAllocate.toLocaleString('en-US')}
            </Typography>
          </Stack>
        )}
        {targetAdditionalAllocation > 0 && (
          <Stack
            justifyContent="flex-start"
            alignItems="center"
            spacing={10}
            direction="row"
          >
            <Chip
              label="Target Additional Allocation"
              style={{ textTransform: 'uppercase' }}
              chipcolor={ChipColor.warning}
            />
            <Typography variant="h5">
              {targetAdditionalAllocation.toLocaleString('en-US')}
            </Typography>
          </Stack>
        )}
        {allocated > 0 && (
          <Stack
            justifyContent="flex-start"
            alignItems="center"
            spacing={10}
            direction="row"
          >
            <Chip
              label="Allocated"
              style={{ textTransform: 'uppercase' }}
              chipcolor={ChipColor.success}
            />
            <Typography variant="h5">
              {allocated.toLocaleString('en-US')}
            </Typography>
          </Stack>
        )}
        {consumed > 0 && (
          <Stack
            justifyContent="flex-start"
            alignItems="center"
            spacing={10}
            direction="row"
          >
            <Chip
              label={consumedLabel ?? 'Consumed'}
              style={{ textTransform: 'uppercase' }}
              chipcolor={
                consumed >= total ? ChipColor.success : ChipColor.warning
              }
            />
            <Typography variant="h5">
              {consumed.toLocaleString('en-US')}
            </Typography>
          </Stack>
        )}
      </Stack>
    </GridLegacy>
  );
};

export function allocationRenderCell({
  params,
  allocated,
  consumed,
  total,
  minimumTotal,
  consumedLabel,
  unitAbbreviation,
}: {
  params: GridRenderCellParams;
  allocated: number;
  consumed: number;
  total: number;
  minimumTotal: number;
  consumedLabel?: string;
  unitAbbreviation?: string;
}) {
  const totalAllocated = allocated + consumed;

  return detailTooltipRenderCell(
    params,
    <AllocationTooltip
      allocated={allocated}
      consumed={consumed}
      total={total}
      minimumTotal={minimumTotal}
      consumedLabel={consumedLabel}
    />,
    <AllocationChip
      totalAllocated={totalAllocated}
      total={total}
      minimumTotal={minimumTotal}
      unitAbbreviation={unitAbbreviation}
    />
  );
}
